import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, Row } from "react-bootstrap";
import {
    AddAccessRightsButton,
    FiltersButton,
    InfoBanner,
    Spinner,
    TableHeader,
    WithRoleAccess,
} from "@services/ui-components";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { CalendarFilterI } from "./constants";
import CalendarWrapper from "./CalendarWrapper";
import CalendarFilterWrapper from "./CalendarFilterWrapper";
import ErrorService from "../../services/ErrorService";
import { useCheckBranchDataForLogBookQuery } from "@me-team/host/main/logbook/graphql/logbook.hooks";
import { useNavigate, useParams } from "react-router-dom";
import ToastComponent from "../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../hooks/useToast";
import NotificationsModal from "./NotificationsForNewCompany/NotificationsModal";
import { currentBranchEmployeeIdVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import AddVacationModal from "../WorkSchedule/modals/AddVacationModal/AddVacationModal";
import CustomDayModal from "../WorkSchedule/modals/CustomDayModal/CustomDayModal";
import { useWorkScheduleAndLogbook } from "../../hooks/useWorkScheduleAndLogbook";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { buildLogbookCalendarAccess } from "./buildLogbookCalendarAccess";
import { useLogbookAccess } from "./useLogbookAccess";

const Logbook: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [showFilters, setShowFilters] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [checkModalShow, setCheckModalShow] = useState<boolean>(false);
    const [notificationOfMissingData, setNotificationOfMissingData] = useState<string>("");
    const [isShowAddEmployeeFromAnotherBranchModal, setIsShowAddEmployeeFromAnotherBranchModal] = useState(false);
    const [keySchedule, setKeySchedule] = useState(0);
    const {
        canCreateLogbookBookingAll,
        canCreateLogbookBookingSelf,
        canEditLogbookBookingAll,
        canEditLogbookBookingSelf,
        canEditLogbookPopoverBookingStatusAll,
        canEditLogbookPopoverBookingStatusSelf,
        canWriteOffLogbookConsumables,
        canCreateScheduleAll,
        canCreateScheduleSelf,
        canCreateCustomDayAll,
        canCreateCustomDaySelf,
        canCreateVacationAll,
        canCreateVacationSelf,
        canOpenEmployeeServicesAll,
        canOpenEmployeeServicesSelf,
        canShowAddEmployeeFromAnotherBranchAction,
        shouldShowOnlyOwnLogbook,
        logbookAccessContract,
    } = useLogbookAccess();

    const {
        refetchEmployeeList,
        handleRefetchEmployeeList,
        handleClickAddVacation,
        handleWeekCellClick,
        handleCustomDay,
        modalData,
        handleCloseVacation,
        handleCustomModalClose,
        editDate,
        cancelModalData,
        setCancelModalData,
        customModalData,
        handleCancelWorkDay,
    } = useWorkScheduleAndLogbook();

    const handleToggleAddEmployeeFromAnotherBranchModal = () =>
        setIsShowAddEmployeeFromAnotherBranchModal((prevState) => !prevState);
    const closeCheckModal = () => setCheckModalShow(false);

    const { loading: loadingCheckBranchData } = useCheckBranchDataForLogBookQuery({
        variables: {
            companyId: Number(currentCompanyId),
            branchId: Number(branchId),
            bookingsLimit: 1,
        },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        fetchPolicy: "cache-and-network",
        onCompleted: (data) => {
            const isBooking = data.user.company.branches[0].bookingsList.length > 0;
            const isAnyActiveServiceEmployeeRelations =
                data.user.company.branches[0].isAnyActiveServiceEmployeeRelations;
            const isAnyActiveServices = data.user.company.branches[0].isAnyActiveServices;
            const isAnyWorkScheduleForWorkingEmployees =
                data.user.company.branches[0].isAnyWorkScheduleForWorkingEmployees;
            if (!isAnyActiveServiceEmployeeRelations || !isAnyActiveServices || !isAnyWorkScheduleForWorkingEmployees) {
                setCheckModalShow(true);
            }
            if (
                isAnyActiveServiceEmployeeRelations &&
                isAnyActiveServices &&
                isAnyWorkScheduleForWorkingEmployees &&
                !isBooking
            ) {
                toggleShowToast(t("The logbook is ready to go!"));
            }
        },
        onError: (error: ApolloError) => {
            console.error(error);
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const handleShowAddEmployeeFromAnotherBranch = () => {
        setIsShowAddEmployeeFromAnotherBranchModal(true);
    };

    const initialValueFilter: CalendarFilterI = {
        statusId: null,
        positionId: null,
        employeeId: shouldShowOnlyOwnLogbook ? (currentBranchEmployeeId ?? null) : null,
        serviceId: null,
    };

    const { showToast, toastText, setShowToast, toggleShowToast } = useToast();
    const isCurrentBranchEmployeePending = shouldShowOnlyOwnLogbook && !currentBranchEmployeeId;
    const logbookAccess = React.useMemo(
        () =>
            buildLogbookCalendarAccess({
                currentBranchEmployeeId: currentBranchEmployeeId ?? null,
                canCreateLogbookBookingAll,
                canCreateLogbookBookingSelf,
                canEditLogbookBookingAll,
                canEditLogbookBookingSelf,
                canEditLogbookPopoverBookingStatusAll,
                canEditLogbookPopoverBookingStatusSelf,
                canWriteOffLogbookConsumables,
                canCreateScheduleAll,
                canCreateScheduleSelf,
                canCreateCustomDayAll,
                canCreateCustomDaySelf,
                canCreateVacationAll,
                canCreateVacationSelf,
                canOpenEmployeeServicesAll,
                canOpenEmployeeServicesSelf,
            }),
        [
            canCreateLogbookBookingAll,
            canCreateLogbookBookingSelf,
            canCreateScheduleAll,
            canCreateScheduleSelf,
            canCreateCustomDayAll,
            canCreateCustomDaySelf,
            canCreateVacationAll,
            canCreateVacationSelf,
            canEditLogbookBookingAll,
            canEditLogbookBookingSelf,
            canEditLogbookPopoverBookingStatusAll,
            canEditLogbookPopoverBookingStatusSelf,
            canOpenEmployeeServicesAll,
            canOpenEmployeeServicesSelf,
            canWriteOffLogbookConsumables,
            currentBranchEmployeeId,
        ]
    );

    useEffect(() => {
        document.addEventListener("click", handleClickAddVacation);
        window.addEventListener("weekCellClickCustomDay", handleWeekCellClick);
        window.addEventListener("editCustomDayModal", handleCustomDay);
        window.addEventListener("onCancelWorkDay", handleCancelWorkDay);

        return () => {
            document.removeEventListener("click", handleClickAddVacation);
            window.addEventListener("weekCellClickCustomDay", handleWeekCellClick);
            window.removeEventListener("editCustomDayModal", handleCustomDay);
            window.removeEventListener("onCancelWorkDay", handleCancelWorkDay);
        };
    }, [handleCancelWorkDay, handleClickAddVacation, handleCustomDay, handleWeekCellClick]);

    const [filters, setFilters] = useState<CalendarFilterI>(initialValueFilter);
    useEffect(() => {
        if (!shouldShowOnlyOwnLogbook || !currentBranchEmployeeId) return;

        setFilters((prev) =>
            prev.employeeId === currentBranchEmployeeId ? prev : { ...prev, employeeId: currentBranchEmployeeId }
        );
    }, [currentBranchEmployeeId, shouldShowOnlyOwnLogbook]);

    const handleCreateRecord = () => {
        navigate(`/logbook/add-record/${branchId}`);
    };

    const toggleFilters = () => setShowFilters(!showFilters);

    if (loadingCheckBranchData || logbookAccessContract.isLogbookAccessPending || isCurrentBranchEmployeePending) {
        return <Spinner />;
    }

    return (
        <>
            <Row className="mb-9">
                <div>
                    <InfoBanner>
                        <p className="fs-7 mb-0">
                            {t("On this page, you can create, view, and edit customer records for the masters.")}
                        </p>
                    </InfoBanner>

                    <TableHeader
                        title={t("Logbook Journal")}
                        colSize={7}
                        btnColSize={5}
                        buttons={
                            <Row className="gx-3 justify-content-lg-end mt-3 mt-lg-0 align-items-center">
                                {canShowAddEmployeeFromAnotherBranchAction ? (
                                    <WithRoleAccess permission={CompanyAccess.AccessManageCompanyEmployees}>
                                        <Col lg={2} className="btn-width">
                                            <AddAccessRightsButton onShow={handleShowAddEmployeeFromAnotherBranch} />
                                        </Col>
                                    </WithRoleAccess>
                                ) : null}
                                <WithRoleAccess permission={CompanyAccess.LogbookBookingCreate}>
                                    <Col xs lg={5}>
                                        <Button
                                            variant="green"
                                            className="w-100 text-white text-truncate"
                                            onClick={handleCreateRecord}
                                        >
                                            <i className="bi bi-plus-lg fw-bold me-1 w-100 h-100"></i>
                                            {t("Add record")}
                                        </Button>
                                    </Col>
                                </WithRoleAccess>
                                <Col xs={3}>
                                    <FiltersButton
                                        filter={filters}
                                        showFilters={showFilters}
                                        onClick={toggleFilters}
                                        classesText="d-none d-xxl-inline-block"
                                    />
                                </Col>
                            </Row>
                        }
                        showFilters={showFilters}
                        calendarView
                    >
                        <CalendarFilterWrapper
                            currentBranchEmployeeId={currentBranchEmployeeId ?? null}
                            shouldShowOnlyOwnLogbook={shouldShowOnlyOwnLogbook}
                            setFilters={setFilters}
                            setNotificationOfMissingData={setNotificationOfMissingData}
                        />
                    </TableHeader>
                </div>

                {modalData.show && (
                    <AddVacationModal
                        show={modalData.show}
                        handleClose={handleCloseVacation}
                        employeeId={modalData.employeeId}
                        forceRefresh={handleRefetchEmployeeList}
                        toggleShowToast={toggleShowToast}
                        edit={modalData.edit}
                        editDate={editDate}
                    />
                )}

                {cancelModalData.show && (
                    <AddVacationModal
                        show={cancelModalData.show}
                        handleClose={() =>
                            setCancelModalData({
                                show: false,
                                date: null,
                                resource: null,
                                employeeId: null,
                                edit: null,
                            })
                        }
                        employeeId={cancelModalData.employeeId}
                        forceRefresh={handleRefetchEmployeeList}
                        toggleShowToast={toggleShowToast}
                        edit={cancelModalData.edit}
                        isCancel={true}
                        editDate={cancelModalData.date}
                    />
                )}

                <CalendarWrapper
                    filters={filters}
                    isShowAddEmployeeFromAnotherBranchModal={isShowAddEmployeeFromAnotherBranchModal}
                    onShowAddEmployeeFromAnotherBranchModal={handleToggleAddEmployeeFromAnotherBranchModal}
                    setNotificationOfMissingData={setNotificationOfMissingData}
                    notificationOfMissingData={notificationOfMissingData}
                    refetchEmployeeList={refetchEmployeeList}
                    handleRefetchEmployeeList={handleRefetchEmployeeList}
                    shouldShowOnlyOwnLogbook={shouldShowOnlyOwnLogbook}
                    logbookAccess={logbookAccess}
                    key={keySchedule}
                />
                {isErrorModalOpen ? (
                    <ErrorModalComponent
                        i18n={i18next}
                        onClose={() => {
                            setIsErrorModalOpen(null);
                        }}
                        isOpen={!!isErrorModalOpen}
                        currentError={isErrorModalOpen}
                    />
                ) : null}
            </Row>

            <NotificationsModal
                show={checkModalShow}
                handleClose={closeCheckModal}
                title="Getting started with the logbook"
            />
            {customModalData && (
                <CustomDayModal
                    show={customModalData.show}
                    toggleModal={handleCustomModalClose}
                    date={customModalData?.date?.c}
                    resource={customModalData.resource}
                    forceRefresh={() => setKeySchedule((prev) => ++prev)}
                    toggleShowToast={toggleShowToast}
                />
            )}

            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default Logbook;
